import { apiClient } from '@/api/client'
import { ApiError, parseProblemDetails } from '@/api/problemDetails'
import type { components } from '@/api/schema'

import type {
  DocumentClassification,
  DocumentImport,
  KnowledgeDocuments,
  WebsiteDiscovery,
} from './types'

type Tenant = components['schemas']['TenantResponse']

/**
 * Os endpoints de Knowledge Auto-Discovery (SS-02) tem `.Produces<T>()` no OpenAPI, entao
 * `result.data` ja vem tipado pelo `openapi-fetch`. So resta o `undefined` defensivo.
 */
function unexpectedFormatError(status: number): ApiError {
  return new ApiError({
    title: 'A resposta da Api nao tem o formato esperado.',
    status,
    errorCode: 'internal_error',
    correlationId: '',
  })
}

/**
 * Adiciona/dispara uma fonte de aprendizado. Com uma `url` http(s), aprende com aquela URL (uma fonte
 * por URL); sem url (undefined/vazio), usa o site cadastrado do tenant (`Tenant.Website`). Erros
 * conhecidos chegam via `ApiError.problem.errorCode`: `invalid_url` (URL malformada) e `website_not_set`
 * (sem url e sem site cadastrado). 202 devolve a `WebsiteDiscoveryResponse` da fonte.
 */
export async function startDiscovery(tenantId: string, url?: string): Promise<WebsiteDiscovery> {
  const trimmed = url?.trim()
  const result = await apiClient.POST('/api/v1/tenants/{tenantId}/knowledge/discovery', {
    params: { path: { tenantId } },
    body: { url: trimmed !== undefined && trimmed.length > 0 ? trimmed : null },
  })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return result.data
}

/** Fontes de aprendizado do site (uma por URL) + progresso de cada uma, para o polling da UI. */
export async function listDiscoverySources(tenantId: string): Promise<WebsiteDiscovery[]> {
  const result = await apiClient.GET('/api/v1/tenants/{tenantId}/knowledge/discovery', {
    params: { path: { tenantId } },
  })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return result.data.sources
}

/** Remove uma fonte de aprendizado (idempotente no backend: 204 mesmo se ja nao existir). */
export async function deleteDiscoverySource(tenantId: string, jobId: string): Promise<void> {
  const result = await apiClient.DELETE('/api/v1/tenants/{tenantId}/knowledge/discovery/{jobId}', {
    params: { path: { tenantId, jobId } },
  })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
}

/**
 * Define/atualiza o site da organizacao (SS-02): habilita o "Ensine seu agente" para quem pulou o site
 * no onboarding. O servidor normaliza/valida; 400 `invalid_website` retorna via `ApiError.problem.errorCode`.
 */
export async function updateWebsite(tenantId: string, website: string): Promise<Tenant> {
  const result = await apiClient.PATCH('/api/v1/tenants/{tenantId}', {
    params: { path: { tenantId } },
    body: { website },
  })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return result.data
}

/**
 * Envia um arquivo (.txt/.md/.pdf/.docx/.xlsx) para o agente aprender. Endpoint multipart/form-data:
 * o corpo vai como `FormData` e o `bodySerializer` identidade impede o openapi-fetch de serializar
 * como JSON — o browser gera o boundary do multipart sozinho e nao setamos Content-Type. O schema
 * `IFormFile` do OpenAPI tipa o corpo como `string` (binario), dai a assercao restrita a esta fronteira.
 * 202 devolve o `DocumentImportResponse` (id do import + status inicial) para acompanhar o progresso.
 * Erros conhecidos chegam via `ApiError.problem` (`file_too_large`/413, `unsupported_or_mismatched_file`/415, etc).
 */
/**
 * Teto de tempo do POST de upload (nao do processamento — o 202 volta assim que o job e enfileirado, o processamento
 * corre no Worker). Sem isto, uma requisicao pendurada (rede/proxy) deixaria o item preso em "Enviando" para sempre;
 * ao estourar, viramos um erro claro (`upload_timeout`) que a UI mostra em vez do spinner infinito.
 * O teto cobre a transferencia INTEIRA do corpo (ate 10 MiB), entao precisa acomodar uplinks lentos: 5 min = ~35 KB/s
 * para 10 MiB, folgado para conexoes moveis/congestionadas, sem abortar um upload legitimo em progresso.
 */
const UPLOAD_TIMEOUT_MS = 300_000

export async function uploadDocument(
  tenantId: string,
  file: File,
  classification: DocumentClassification = 'Public',
): Promise<DocumentImport> {
  const formData = new FormData()
  formData.append('file', file, file.name)
  // Default (omitido) = Public no backend; so enviamos o campo quando "uso interno".
  if (classification === 'Internal') {
    formData.append('classification', 'Internal')
  }

  const controller = new AbortController()
  const timeoutId = setTimeout(() => {
    controller.abort()
  }, UPLOAD_TIMEOUT_MS)

  try {
    const result = await apiClient.POST('/api/v1/tenants/{tenantId}/knowledge/documents', {
      params: { path: { tenantId } },
      body: formData as unknown as string,
      bodySerializer: (body) => body,
      signal: controller.signal,
    })

    if (!result.response.ok) {
      throw new ApiError(parseProblemDetails(result.error, result.response.status))
    }

    if (result.data === undefined) {
      throw unexpectedFormatError(result.response.status)
    }

    return result.data
  } catch (error) {
    // AbortController (timeout) ou falha de rede: erro claro e tipado — nunca deixar o upload pendurado.
    if (!(error instanceof ApiError)) {
      throw new ApiError({
        title: 'O envio demorou mais que o esperado.',
        status: 0,
        errorCode: controller.signal.aborted ? 'upload_timeout' : 'network_error',
        correlationId: '',
      })
    }
    throw error
  } finally {
    clearTimeout(timeoutId)
  }
}

/** Status/progresso da importacao de um arquivo enviado, para o polling da UI (queued/importing/ready/failed). */
export async function getImportStatus(tenantId: string, importId: string): Promise<DocumentImport> {
  const result = await apiClient.GET(
    '/api/v1/tenants/{tenantId}/knowledge/documents/imports/{importId}',
    {
      params: { path: { tenantId, importId } },
    },
  )

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return result.data
}

/** Exclui um documento aprendido e os trechos que o agente aprendeu dele (idempotente: 204). */
export async function deleteDocument(tenantId: string, documentId: string): Promise<void> {
  const result = await apiClient.DELETE(
    '/api/v1/tenants/{tenantId}/knowledge/documents/{documentId}',
    {
      params: { path: { tenantId, documentId } },
    },
  )

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }
}

/** Documentos aprendidos pelo agente + contagem por status. */
export async function listKnowledgeDocuments(tenantId: string): Promise<KnowledgeDocuments> {
  const result = await apiClient.GET('/api/v1/tenants/{tenantId}/knowledge/documents', {
    params: { path: { tenantId } },
  })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return result.data
}
